import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {LucideCheck, LucideChevronDown} from "lucide-react";
import {BodyRow, BodyRowMapper, HeaderCell, HeaderRow, Table} from "../../components/Table";
import {doFetch} from "../../util/fetch";
import {relativeDateAndTime} from "../../util/timeConverter";
import {AdminUser, SUBSCRIPTION_TIER_OPTIONS, subscriptionTierLabel} from "./interfaces";

export default function UsersTable(props: {
    users?: AdminUser[]
    loading: boolean
    onTierChanged: (userId: string, tier: number) => void
}) {
    return <Table defaultSortKey="name">
        <HeaderRow>
            <HeaderCell sortKey="id">ID</HeaderCell>
            <HeaderCell sortKey="name">Name</HeaderCell>
            <HeaderCell sortKey="email">Email</HeaderCell>
            <HeaderCell sortKey="steamId">Steam ID</HeaderCell>
            <HeaderCell sortKey="discordId">Discord ID</HeaderCell>
            <HeaderCell sortKey="created">Created</HeaderCell>
            <HeaderCell sortKey="subscriptionTier">Tier</HeaderCell>
            <HeaderCell sortKey="subscriptionNextUpdateTime">Next sub update</HeaderCell>
        </HeaderRow>
        <BodyRowMapper data={props.users} placeholder={props.loading ? "Loading users..." : "No users"}
                       map={(user: AdminUser) =>
                           <BodyRow key={user.id}>
                               <td className="font-mono text-xs text-neutral-400 select-text cursor-text">{user.id}</td>
                               <td className="font-medium">{user.name}</td>
                               <td className="text-neutral-300 select-text cursor-text">{user.email}</td>
                               <td className="font-mono text-xs text-neutral-400 select-text cursor-text">{user.steamId ?? "-"}</td>
                               <td className="font-mono text-xs text-neutral-400 select-text cursor-text">{user.discordId ?? "-"}</td>
                               <td className="text-neutral-400">{relativeDateAndTime(user.created)}</td>
                               <td>
                                   <TierMenu user={user} onTierChanged={props.onTierChanged}/>
                               </td>
                               <td className="text-neutral-400">
                                   {user.subscriptionNextUpdateTime > 0 ? relativeDateAndTime(user.subscriptionNextUpdateTime) : "Never"}
                               </td>
                           </BodyRow>
                       }/>
    </Table>
}

// Click-to-open popup menu (rendered in a portal so the table's overflow-auto doesn't clip it).
// Requires a deliberate open + select, so the tier can't be changed by an accidental scroll/click.
function TierMenu(props: { user: AdminUser, onTierChanged: (userId: string, tier: number) => void }) {
    const [open, setOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string>()
    const [pos, setPos] = useState<{ top: number, left: number }>()
    const buttonRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return

        function onPointerDown(e: MouseEvent) {
            const target = e.target as Node
            if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return
            setOpen(false)
        }

        // The button is anchored with fixed coords, so detach the menu if anything scrolls/resizes.
        function close() {
            setOpen(false)
        }

        document.addEventListener("mousedown", onPointerDown)
        window.addEventListener("resize", close)
        window.addEventListener("scroll", close, true)
        return () => {
            document.removeEventListener("mousedown", onPointerDown)
            window.removeEventListener("resize", close)
            window.removeEventListener("scroll", close, true)
        }
    }, [open])

    function toggle() {
        if (saving) return
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            setPos({top: rect.bottom + 4, left: rect.left})
        }
        setOpen(!open)
    }

    async function selectTier(tier: number) {
        setOpen(false)
        if (tier === props.user.subscriptionTier) return

        const confirmed = window.confirm(
            `Change subscription tier of ${props.user.name} (${props.user.email})\n` +
            `from ${subscriptionTierLabel(props.user.subscriptionTier)} to ${subscriptionTierLabel(tier)}?`
        )
        if (!confirmed) return

        setSaving(true)
        setError(undefined)
        const res = await doFetch("/api/client/admin/users/tier", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({userId: props.user.id, tier}),
        })
        setSaving(false)
        if (res.ok) {
            props.onTierChanged(props.user.id, tier)
        } else {
            setError(res.errorString ?? "Failed to update tier")
        }
    }

    return <div className="flex items-center gap-2">
        <button ref={buttonRef} type="button" disabled={saving} onClick={toggle}
                className="flex items-center gap-1.5 bg-neutral-800 border border-neutral-600 rounded-md px-2 py-1 text-sm hover:bg-neutral-700 disabled:opacity-50">
            {subscriptionTierLabel(props.user.subscriptionTier)}
            <LucideChevronDown size={14} className="text-neutral-400"/>
        </button>
        {saving && <span className="text-xs text-neutral-400">Saving…</span>}
        {error && <span className="text-xs text-red-400" title={error}>Failed</span>}

        {open && pos && createPortal(
            <div ref={menuRef} style={{top: pos.top, left: pos.left}}
                 className="fixed z-50 min-w-[12rem] rounded-md border border-neutral-600 bg-neutral-800 shadow-lg py-1 text-sm">
                <div className="px-3 py-1 text-xs text-neutral-500 select-none">Change subscription tier</div>
                {SUBSCRIPTION_TIER_OPTIONS.map(option => {
                    const active = option.value === props.user.subscriptionTier
                    return <button key={option.value} type="button" onClick={() => selectTier(option.value)}
                                   className={"w-full flex items-center justify-between gap-3 px-3 py-1.5 text-left hover:bg-neutral-700 " + (active ? "text-white" : "text-neutral-300")}>
                        {option.label}
                        {active && <LucideCheck size={14} className="text-green-400"/>}
                    </button>
                })}
            </div>,
            document.body)}
    </div>
}
